/* eslint-disable @typescript-eslint/naming-convention */
import type { TypingTickerProps as CDKTypingTickerProps, TypingTickerCursorProps } from '@lemonade-hq/cdk';
import { TypingTicker as CDKTypingTicker } from '@lemonade-hq/cdk';
import type { Milliseconds } from '@lemonade-hq/ts-helpers';
import { assignInlineVars } from '@vanilla-extract/dynamic';
import { clsx } from 'clsx';
import { motion } from 'motion/react';
import React, { useId, useMemo } from 'react';
import type { ComponentProps, PropsWithChildren } from 'react';
import { textPreWrap } from '../../styles/utilities.css';
import { Layout } from '../Layout/Layout';
import * as styles from './TypingTicker.css';

interface CursorDelayProps {
  readonly entranceDelay?: Milliseconds;
}

type CursorProps = ComponentProps<'div'> &
  CursorDelayProps & {
    readonly hidden?: boolean;
    readonly blinking?: boolean;
  };

// Replacement for `TypingTicker`'s `Container` component so we won't use styled components
const Container: React.FC<PropsWithChildren> = ({ children }) => <Layout margin="auto">{children}</Layout>;

// Replacement for `TypingTicker`'s `TextWrapper` component so we won't use styled components
const TextWrapper: React.FC<ComponentProps<'div'>> = ({ className, children }) => (
  <Layout className={clsx(textPreWrap, className)} display="inline-block">
    {children}
  </Layout>
);

const CURSOR_HIDE_DELAY: Milliseconds = 220;
const CURSOR_ENTER_DELAY: Milliseconds = 100;

export const TypingTickerCursor: React.FC<CursorProps> = ({ hidden, blinking, className, entranceDelay = 0 }) => (
  <motion.span
    animate={hidden ? 'hidden' : 'visible'}
    aria-hidden="true"
    className={clsx(styles.cursor({ blinking }), className)}
    data-testid="cursor"
    initial="hidden"
    style={
      hidden
        ? assignInlineVars({ [styles.cursorOverlayOpacityVar]: '0' })
        : assignInlineVars({ [styles.cursorOverlayOpacityVar]: '1' })
    }
    variants={{
      visible: {
        opacity: 1,
        scaleY: 1,
        transition: {
          duration: 0.2,
          delay: (entranceDelay + CURSOR_ENTER_DELAY) / 1000,
        },
      },
      hidden: {
        opacity: 0,
        scaleY: 0,
        transition: {
          duration: 0.2,
          delay: CURSOR_HIDE_DELAY / 1000,
        },
      },
    }}
  >
    &nbsp;
  </motion.span>
);

export const EmptyDiv: React.FC = () => <div aria-hidden="true" />;

export const PreventTextJump: React.FC<PropsWithChildren<{ readonly text: string; readonly enabled: boolean }>> = ({
  children,
  text,
  enabled,
}) => {
  // eslint-disable-next-line react/jsx-no-useless-fragment
  if (!enabled) return <>{children}</>;

  return (
    <Layout position="relative" width="100%">
      {/* This is a hack to make the text not jump when it's moving to a different row */}
      <span className={styles.ghostText} data-testid="ghostText">
        {text}
      </span>
      <Layout height="100%" left="0" position="absolute" top="0">
        {children}
      </Layout>
    </Layout>
  );
};

export interface TypingTickerProps extends Omit<CDKTypingTickerProps, 'cursor' | 'CursorComponent' | 'textKey'> {
  readonly preventTextJump?: boolean;
  readonly text: string;
  readonly cursor?:
    | true
    | {
        readonly entranceDelay?: Milliseconds;
      };
  readonly children?: React.ReactNode;
}

const TRANSLATED_CLASS_REGEXP = /translated/;

export const TypingTicker: React.FC<TypingTickerProps> = ({
  cursor: cursorProp,
  delayBetweenDifferentTexts,
  maxCharsPerIteration,
  minCharsPerIteration,
  maxTypingInterval,
  minTypingInterval,
  text,
  preventTextJump = true,
  children,
}) => {
  const spanId = useId();
  const isTranslated = TRANSLATED_CLASS_REGEXP.exec(document.documentElement.className);

  const cursor = useMemo(() => {
    return typeof cursorProp === 'boolean' ? {} : cursorProp;
  }, [cursorProp]);

  const CursorComponent = useMemo(
    () =>
      ({ blinking }: TypingTickerCursorProps) => (
        <TypingTickerCursor blinking={blinking} entranceDelay={cursor?.entranceDelay} hidden={blinking} />
      ),
    [cursor],
  );

  return (
    <PreventTextJump enabled={preventTextJump} text={text}>
      <CDKTypingTicker
        Container={Container}
        CursorComponent={cursor == null ? EmptyDiv : CursorComponent}
        /* @ts-expect-error - TextWrapperProps are wrong to not include children */
        TextWrapper={TextWrapper}
        delayBetweenDifferentTexts={delayBetweenDifferentTexts}
        maxCharsPerIteration={isTranslated ? text.length : maxCharsPerIteration}
        maxTypingInterval={isTranslated ? 0 : maxTypingInterval}
        minCharsPerIteration={isTranslated ? text.length : minCharsPerIteration}
        minTypingInterval={isTranslated ? 0 : minTypingInterval}
        textKey={text}
      >
        {children}
        <span id={spanId}>&#8203;</span>
      </CDKTypingTicker>
    </PreventTextJump>
  );
};
